import { newestChangelogDate } from '@/lib/changelog';
import { cn } from '@/lib/utils';
import { Newspaper } from 'lucide-react';
import { useCallback, useState } from 'react';
import { WhatsNewDrawer } from './WhatsNewDrawer';
import {
  CHANGELOG_ENTRIES,
  hasUnreadChangelog,
  readWhatsNewSeen,
  writeWhatsNewSeen,
} from './whats_new';

interface Props {
  /** `rail` = icon-only square in the collapsed desktop sidebar; `rail-expanded` =
   *  full-width labeled row matching the expanded rail's nav rows; `menu` =
   *  full-width row in the mobile menu. */
  variant: 'rail' | 'rail-expanded' | 'menu';
  /** Mobile menu closes itself when the drawer opens. */
  onOpen?: () => void;
}

/**
 * "What's new" entry point. Owns the drawer + the `whatsNewSeen` bookkeeping
 * so the AppShell only needs to place it. The unread dot shows when the
 * newest changelog date is newer than the stored date; it is HIDDEN when
 * localStorage is unusable (we can't remember a dismissal, so we don't nag).
 */
export function WhatsNewButton({ variant, onOpen }: Props) {
  const [seen, setSeen] = useState<string | null | undefined>(() => readWhatsNewSeen());
  const [open, setOpen] = useState(false);
  // Snapshot of what the user had seen BEFORE this open, so "New" markers
  // stay visible while the drawer is open even though we've marked it seen.
  const [lastSeenAtOpen, setLastSeenAtOpen] = useState<string | null>(null);

  const unread = hasUnreadChangelog(CHANGELOG_ENTRIES, seen);

  const handleOpen = useCallback(() => {
    setLastSeenAtOpen(seen ?? null);
    setOpen(true);
    onOpen?.();
    const newest = newestChangelogDate(CHANGELOG_ENTRIES);
    if (newest !== null && seen !== undefined) {
      if (writeWhatsNewSeen(newest)) setSeen(newest);
    }
  }, [seen, onOpen]);

  const handleClose = useCallback(() => setOpen(false), []);

  const label = unread ? "What's new (unread)" : "What's new";

  return (
    <>
      {variant === 'rail' ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label={label}
          className="group relative grid size-10 place-items-center rounded-lg text-muted-foreground press hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <Newspaper className="size-[18px]" strokeWidth={1.75} />
          {unread && (
            <span
              data-testid="whats-new-dot"
              className="absolute right-2 top-2 size-2 rounded-full bg-primary ring-2 ring-sidebar"
            />
          )}
          <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background opacity-0 shadow-md transition-opacity group-hover:opacity-100">
            What's new
          </span>
        </button>
      ) : variant === 'rail-expanded' ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label={label}
          className="group relative flex h-[38px] w-full shrink-0 items-center gap-3 rounded-[9px] px-3 text-[13.5px] font-medium tracking-tight text-muted-foreground press hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <span className="relative grid shrink-0 place-items-center">
            <Newspaper className="size-[18px]" strokeWidth={1.75} />
            {unread && (
              <span
                data-testid="whats-new-dot"
                className="absolute -right-1 -top-1 size-2 rounded-full bg-primary ring-2 ring-sidebar"
              />
            )}
          </span>
          <span className="whitespace-nowrap">What's new</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          aria-label={label}
          className={cn(
            'flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-foreground/85 hover:bg-secondary',
          )}
        >
          <span className="relative grid place-items-center">
            <Newspaper className="size-[18px]" strokeWidth={1.75} />
            {unread && (
              <span
                data-testid="whats-new-dot"
                className="absolute -right-1 -top-1 size-2 rounded-full bg-primary ring-2 ring-background"
              />
            )}
          </span>
          What's new
        </button>
      )}
      <WhatsNewDrawer
        open={open}
        entries={CHANGELOG_ENTRIES}
        lastSeen={lastSeenAtOpen}
        onClose={handleClose}
      />
    </>
  );
}
